/**
 * Mesh topology diagram model -- PURE (no React, no fetch), unit-tested by meshTopology.test.ts
 * (`npm run test:unit`) and drawn by components/mesh/MeshTopologyDiagram.tsx. OP#1803.
 *
 * Input: the server's `proposed_topology` (services/mesh_preview). Output: positioned boxes and
 * lines for a hand-drawn SVG:
 *
 *   switch / port  ──solid cable──▶  wired ROOT  ─ ─ dashed expected link ─ ─▶  mesh POINT
 *
 * Rules (owner thread 2026-10-12):
 *   - In a 1-hop mesh every point links to a wired ROOT, never node-to-node. A point->point line
 *     is drawn ONLY when max_hops is 2 and the server's BFS made another point its parent.
 *   - mode "plan": every point link is DASHED (expected), labelled with hop + RSSI/quality when
 *     known ("not measured" otherwise).
 *   - mode "observed" (after deploy): links the APs REPORT are SOLID; where the expected parent
 *     differs, the expected link stays dashed next to it and the AP is flagged.
 *   - A point with no proposable parent is drawn unconnected and flagged critical (its block
 *     check names the reason).
 *
 * Colour is a status TONE only (healthy / warn / critical / info / unknown); the component maps
 * tones to the status-ramp tokens. Nothing here picks brand colours.
 */
import type {
  MeshCheckSeverity, MeshLinkQuality, MeshProposedTopology, MeshTopologyCheck, MeshTopologyNode,
} from "@/lib/api/siteMesh";

export type Tone = "healthy" | "warn" | "critical" | "info" | "unknown";
export type DiagramMode = "plan" | "observed";

export const BOX_W = 168;
export const BOX_H = 48;
export const SWITCH_H = 40;
export const COL_GAP = 24;
export const ROW_GAP = 72;
export const PAD = 16;

export interface DiagramBox {
  id: string;
  kind: "switch" | "ap";
  serial: string | null;
  label: string;
  sublabel: string;
  tone: Tone;
  /** Short flag under the box ("differs from plan", "no path to a root"); null for none. */
  flag: string | null;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface DiagramLine {
  id: string;
  from: string;
  to: string;
  /** cable = switch->root wire; expected = the plan's link; observed = what the APs report. */
  kind: "cable" | "expected" | "observed";
  dashed: boolean;
  label: string;
  tone: Tone;
}

export interface DiagramModel {
  boxes: DiagramBox[];
  lines: DiagramLine[];
  width: number;
  height: number;
}

/** good -> healthy, fair -> warn, poor -> critical, else neutral. */
export function linkTone(q: MeshLinkQuality | null | undefined): Tone {
  if (q === "good") return "healthy";
  if (q === "fair") return "warn";
  if (q === "poor") return "critical";
  return "unknown";
}

function fmtDbm(rssi: number | null | undefined): string | null {
  return typeof rssi === "number" ? `${Math.round(rssi)} dBm` : null;
}

/** "hop 1 · -61 dBm (good)" / "hop 1 · not measured" / "hop 1 · any of 2 roots". */
export function linkLabel(
  hop: number | null | undefined,
  rssi: number | null | undefined,
  quality: MeshLinkQuality | null | undefined,
  candidates = 1,
): string {
  const parts: string[] = [];
  if (typeof hop === "number") parts.push(`hop ${hop}`);
  const dbm = fmtDbm(rssi);
  if (dbm) parts.push(quality && quality !== "unknown" && quality !== "n/a" ? `${dbm} (${quality})` : dbm);
  else parts.push(candidates > 1 ? `not measured · any of ${candidates} roots` : "not measured");
  return parts.join(" · ");
}

/** Node tone: blocked critical > differs warn > role (root healthy, point info). */
function nodeTone(n: MeshTopologyNode, blockedSerials: Set<string>, mode: DiagramMode): Tone {
  if (blockedSerials.has(n.serial)) return "critical";
  if (mode === "observed" && n.differs.length) return "warn";
  if (n.expected_role === "root") return "healthy";
  if (n.expected_role === "point") return "info";
  return "unknown";
}

const ROLE_SHORT: Record<string, string> = { root: "Wired root", point: "Mesh point", off: "Mesh off" };

function switchKey(n: MeshTopologyNode): string {
  const sw = n.uplink.switch_serial || n.uplink.switch;
  return sw ? `sw:${sw}` : "sw:unknown";
}

/** Layout tree node (internal). */
interface TNode {
  id: string;
  children: TNode[];
  depth: number;
  x?: number;
}

/**
 * Build the diagram. Tiers: 0 switches, 1 wired roots, 2 hop-1 points, 3 hop-2 points.
 * Leaves get consecutive columns; a parent is centred over its children (tidy tree).
 */
export function buildDiagram(topo: MeshProposedTopology, mode: DiagramMode = "plan"): DiagramModel {
  const nodes = topo.nodes.filter((n) => n.expected_role !== "off");
  const bySerial = new Map(nodes.map((n) => [n.serial, n]));
  const blocked = new Set(
    topo.checks.filter((c) => c.severity === "block" && c.serial).map((c) => c.serial as string),
  );
  const roots = nodes.filter((n) => n.expected_role === "root");
  const points = nodes.filter((n) => n.expected_role === "point");

  // Parent used for PLACEMENT: observed parent in observed mode (what is real), else expected.
  // Point->point placement only where the server allowed it (max_hops 2; it never proposes one
  // otherwise, and an observed one beyond max_hops is still drawn -- it is the reality to flag).
  const placeParent = (p: MeshTopologyNode): string | null => {
    const obs = mode === "observed" && p.observed.mesh_up ? p.observed.parent : null;
    const parent = obs ?? p.expected_parent;
    return parent && bySerial.has(parent) ? parent : null;
  };

  // Switch tier: one box per distinct switch; roots without LLDP share "LAN (port unknown)".
  const switchIds: string[] = [];
  const switchLabel = new Map<string, { label: string; serial: string | null }>();
  for (const r of roots) {
    const k = switchKey(r);
    if (!switchLabel.has(k)) {
      switchIds.push(k);
      switchLabel.set(k, {
        label: r.uplink.switch ?? "LAN switch",
        serial: r.uplink.switch_serial ?? null,
      });
    }
  }

  const tnodes = new Map<string, TNode>();
  const mk = (id: string, depth: number): TNode => {
    const t: TNode = { id, children: [], depth };
    tnodes.set(id, t);
    return t;
  };
  const top: TNode[] = switchIds.map((k) => mk(k, 0));
  const topById = new Map(top.map((t) => [t.id, t]));
  for (const r of roots) topById.get(switchKey(r))!.children.push(mk(`ap:${r.serial}`, 1));

  // Points: attach breadth-first so a hop-2 point finds its hop-1 parent already placed.
  const pending = [...points].sort(
    (a, b) => (a.expected_hop_count ?? 9) - (b.expected_hop_count ?? 9) || a.name.localeCompare(b.name),
  );
  const orphans: MeshTopologyNode[] = [];
  for (let guard = 0; pending.length && guard < 4; guard++) {
    for (let i = 0; i < pending.length; ) {
      const p = pending[i]!;
      const parent = placeParent(p);
      const parentT = parent ? tnodes.get(`ap:${parent}`) : undefined;
      if (parentT) {
        parentT.children.push(mk(`ap:${p.serial}`, parentT.depth + 1));
        pending.splice(i, 1);
      } else if (!parent) {
        orphans.push(p);
        pending.splice(i, 1);
      } else i++;
    }
  }
  orphans.push(...pending);
  // Unconnected points sit in the first point tier, to the right.
  const orphanT = orphans.map((p) => mk(`ap:${p.serial}`, 2));

  // Tidy-tree x: leaves take consecutive slots; parents centre over their children.
  let slot = 0;
  const place = (t: TNode) => {
    if (!t.children.length) {
      t.x = slot++;
      return;
    }
    t.children.forEach(place);
    t.x = (t.children[0]!.x! + t.children[t.children.length - 1]!.x!) / 2;
  };
  top.forEach(place);
  orphanT.forEach(place);

  const cols = Math.max(1, slot);
  const depthMax = Math.max(1, ...[...tnodes.values()].map((t) => t.depth));
  const width = PAD * 2 + cols * BOX_W + (cols - 1) * COL_GAP;
  const height = PAD * 2 + (depthMax + 1) * BOX_H + depthMax * ROW_GAP + 18;
  const xOf = (t: TNode) => PAD + (t.x ?? 0) * (BOX_W + COL_GAP);
  const yOf = (t: TNode) => PAD + t.depth * (BOX_H + ROW_GAP);

  const boxes: DiagramBox[] = [];
  for (const k of switchIds) {
    const t = tnodes.get(k)!;
    const meta = switchLabel.get(k)!;
    boxes.push({
      id: k, kind: "switch", serial: meta.serial, label: meta.label,
      sublabel: k === "sw:unknown" ? "port unknown" : meta.serial ? "managed switch" : "switch (LLDP)",
      tone: "unknown", flag: null, x: xOf(t), y: yOf(t) + (BOX_H - SWITCH_H) / 2, w: BOX_W, h: SWITCH_H,
    });
  }
  for (const n of nodes) {
    const t = tnodes.get(`ap:${n.serial}`);
    if (!t) continue;
    const hop = n.expected_role === "root" ? "wired" : n.expected_hop_count != null ? `hop ${n.expected_hop_count}` : "no path";
    let flag: string | null = null;
    if (blocked.has(n.serial)) flag = n.expected_role === "point" && !placeParent(n) ? "no path to a root" : "blocked";
    else if (mode === "observed" && n.differs.length) flag = "differs from plan";
    else if (!n.online) flag = "offline";
    boxes.push({
      id: `ap:${n.serial}`, kind: "ap", serial: n.serial, label: n.name,
      sublabel: `${ROLE_SHORT[n.expected_role] ?? n.expected_role} · ${hop}`,
      tone: nodeTone(n, blocked, mode), flag, x: xOf(t), y: yOf(t), w: BOX_W, h: BOX_H,
    });
  }

  const lines: DiagramLine[] = [];
  for (const r of roots) {
    const down = r.uplink.carrier === false;
    lines.push({
      id: `cable:${r.serial}`, from: switchKey(r), to: `ap:${r.serial}`, kind: "cable", dashed: false,
      label: [r.uplink.port ? `port ${r.uplink.port}` : r.uplink.switch ? "port unknown" : null,
        down ? "cable DOWN" : r.uplink.carrier === true ? null : "cable not reported"]
        .filter(Boolean).join(" · "),
      tone: down ? "critical" : "unknown",
    });
  }
  for (const p of points) {
    const observedParent = mode === "observed" && p.observed.mesh_up ? p.observed.parent : null;
    if (observedParent && bySerial.has(observedParent)) {
      lines.push({
        id: `obs:${p.serial}`, from: `ap:${observedParent}`, to: `ap:${p.serial}`, kind: "observed",
        dashed: false, label: linkLabel(p.observed.hop_count, p.observed.link_rssi, p.observed.quality),
        tone: linkTone(p.observed.quality),
      });
    }
    const exp = p.expected_parent;
    if (exp && bySerial.has(exp) && exp !== observedParent) {
      lines.push({
        id: `exp:${p.serial}`, from: `ap:${exp}`, to: `ap:${p.serial}`, kind: "expected", dashed: true,
        label: (observedParent ? "expected · " : "") + linkLabel(
          p.expected_hop_count, p.link_rssi, p.link_quality,
          p.link_source === "assumed" ? p.candidate_roots.length : 1),
        tone: p.link_source === "measured" ? linkTone(p.link_quality) : "unknown",
      });
    }
  }
  return { boxes, lines, width, height };
}

/** "observed" once any member reports the mesh up, else "plan". */
export function diagramMode(topo: MeshProposedTopology | null | undefined): DiagramMode {
  return topo?.nodes.some((n) => n.observed.mesh_up) ? "observed" : "plan";
}

/** Checks grouped by severity, block first. */
export function checksBySeverity(checks: MeshTopologyCheck[]): Record<MeshCheckSeverity, MeshTopologyCheck[]> {
  const out: Record<MeshCheckSeverity, MeshTopologyCheck[]> = { block: [], warn: [], info: [] };
  for (const c of checks) out[c.severity]?.push(c);
  return out;
}

export const SEVERITY_TITLE: Record<MeshCheckSeverity, string> = {
  block: "Must fix before applying",
  warn: "Review before applying",
  info: "Good to know",
};

export function severityTone(s: MeshCheckSeverity): Tone {
  return s === "block" ? "critical" : s === "warn" ? "warn" : "info";
}

/**
 * Why Apply is disabled, as plain sentences (empty = it may be enabled). Block checks first,
 * then any other plan refusal the topology does not already name.
 */
export function applyBlockers(
  topo: MeshProposedTopology | null | undefined,
  refusals: { code: string; message: string; serial: string | null }[],
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const c of topo?.checks ?? []) {
    if (c.severity !== "block") continue;
    seen.add(`${c.code}|${c.serial ?? ""}`);
    out.push(c.message);
  }
  for (const r of refusals) {
    if (seen.has(`${r.code}|${r.serial ?? ""}`)) continue;
    out.push(r.message);
  }
  return out;
}
